<script setup lang="ts">
import Icon from '../Icon/Icon.vue';
import ShopCartPopper from './components/ShopCartPopper/ShopCartPopper.vue';
import { useShopCartStore } from '../../store/headerState/shopCartState.ts';
import { ref, watch, onBeforeMount, computed, onMounted, onUnmounted, nextTick } from 'vue';
import getCartFromUrl from '../../api/shopping/getCartFromUrl';
import type { PriceItem as Item, CurrencyFormat } from '../../types/priceItem.ts';
import updateQuantityToUrl from '../../api/shopping/updateQuantityToUrl';
import { formatMoney } from 'accounting-js';
import type { StoreShopConfig } from '../../types/storeConfig.ts';
import { useStoreTranslations } from '../../store/translations/translations.ts';
import { useStoreConfiguration } from '../../store/configuration/configuration.ts';
import { LinkTarget } from '../../types/link.ts';
import CustomEvents from '../../globals/constants/customEvents.js';
import removeItemToUrl from '../../xhr/shopCart/removeItemToUrl.js';
import { isMobile } from '../../utils/breakpoints.js';
import { initNexwiseProductWizardBridge } from '../../utils/nexwiseProductWizardBridge';
import { post } from '../../xhr/post';
import withBaseUrl from '../../xhr/withBaseUrl';
import Notifications from '../Notifications/Notifications.vue';

const shopCartStore = useShopCartStore();
const storeConfiguration = useStoreConfiguration();

type Props = {
  config: StoreShopConfig;
  iFramePage: string;
  currentLanguage: string;
  currentCountry: string;
  isUserLoggedIn: boolean;
};

type CartData = {
  pricesum: number;
  quantity: number;
  items: Item[];
};

type ResponseError = {
  success: boolean;
  error_message: string;
};

const props = defineProps<Props>();

const storeTranslations = useStoreTranslations();

const isShopCartOpen = ref(false);
const cartData = ref<CartData>({
  pricesum: 0,
  quantity: 0,
  items: [],
});

const loading = ref(true);
const isUpdating = ref(false);
const isInitialized = ref(false);
const selectOptions = ref();
const priceSumItems = ref<number[]>([]);
const totalPrice = ref('0');
const hasError = ref<boolean>(false);
const errorText = ref<string | undefined>(undefined);
const amountMessage = ref('');
const showAmountMessage = ref(false);
const successMessage = ref<string | undefined>(undefined);
const isItemLoading = ref<{ id: string; isLoading: boolean } | undefined>(undefined);
const shouldFlashPopper = ref<boolean>(false);
const newItem = ref<Item | null>(null);
const formatConfig = ref<CurrencyFormat>({
  currency: props.config.currencyCode,
  currencyPosition: props.config.currencyPosition,
  decimalSep: props.config.decimalSeparator,
  thousandSep: props.config.thousandSeparator,
  precisionValue: props.config.precision.toString(),
});
const selectOptionMax = ref<number>(Number(props.config.selectOptionMax || 225));

const shippingLink = computed(() => ({
  id: props.config.shippingInfoUrl._id,
  url: props.config.shippingInfoUrl.route,
  label:
    props.config.shippingInfoUrl.displayName ||
    (storeTranslations.translations?.['shopCart.shipping'] ?? ''),
  target: LinkTarget.self,
}));

const shopCartCheckoutUrl = computed(() => ({
  id: 'checkout',
  url:
    shopCartUrl.value.url + (props.isUserLoggedIn ? '&actionStep=shipping' : '&actionStep=login'), // https://etecture.atlassian.net/browse/WIKA-3991 & https://etecture.atlassian.net/browse/WIKA-4164
  label: storeTranslations.translations?.['shopCart.checkout'] ?? '',
}));

const shopCartUrl = computed(() => ({
  id: 'shopCartUrl',
  url: props.iFramePage + props.config.showCartAction,
  label: storeTranslations.translations?.['shopCart.showCart'] ?? '',
}));

const shouldDisableButton = computed(() => {
  return window.location.href.indexOf('shop_iframe') > -1;
});

const cartUrl = ref<string>(props.config.getCartUrl);
const updateUrl = ref<string>(props.config.updateQuantityURL);
const removeUrl = ref<string>(props.config.removeItemUrl);
let disposeNexwiseBridge: null | (() => void) = null;

const range = (start, end) =>
  [...Array(Number(end) - start + 1)].map((_, i) => start + i).map(String);

selectOptions.value = computed(() => {
  return range(1, selectOptionMax.value);
});

const toggleCartClick = () => {
  shopCartStore.setShopCartOpen(!isShopCartOpen.value);
  if (cartData.value.items?.length === 0 && shopCartUrl.value?.url) {
    window.location.href = `${shopCartUrl.value.url}`;
  }
};

const fetchLowestQuantities = async () => {
  const itemInfoUrl = storeConfiguration.config?.caas_getItemInfoUrl;
  if (!itemInfoUrl || cartData.value.items.length === 0) {
    return;
  }
  try {
    await Promise.all(
      cartData.value.items.map(async (item) => {
        const response = await post<{
          LowestQty?: number;
          HighestQty?: number;
          MultipleQty?: number;
        }>(withBaseUrl({ config: storeConfiguration.config!, url: itemInfoUrl }), {
          itemid: item.id,
          country: props.currentCountry,
          container: props.config.container,
          shopContainer: props.config.shopContainer,
          language: props.currentLanguage?.replace('EN-US', 'EN'),
        }).catch((error) => {
          console.error(error);
        });

        item.lowestQty = Number(response?.LowestQty) || 0;
        item.highestQty = Number(response?.HighestQty) || 0;
        item.multipleQty = Number(response?.MultipleQty) || 0;
      }),
    );
  } catch (error) {
    console.error(error);
  }
};

const getCart = () => {
  isUpdating.value = true;
  getCartFromUrl(cartUrl.value, {
    country: props.currentCountry,
    container: props.config.container,
    shopContainer: props.config.shopContainer,
    language: props.currentLanguage?.replace('EN-US', 'EN'),
  })
    .then((response) => {
      cartData.value = response as CartData;
      return fetchLowestQuantities();
    })
    .then(() => {
      isInitialized.value = true;
    })
    .catch((error) => console.error(`shopCart getCart error: ${error}`))
    .finally(() => {
      loading.value = false;
      isUpdating.value = false;
    });
};

const formatWithCurrency = (value) => {
  return formatMoney(value, {
    symbol: formatConfig.value.currency,
    format: formatConfig.value.currencyPosition,
    decimal: formatConfig.value.decimalSep,
    thousand: formatConfig.value.thousandSep,
    precision: formatConfig.value.precisionValue,
  });
};

const priceSum = ref(formatWithCurrency(0));

const calcSum = () => {
  const sumRaw = priceSumItems.value.reduce((previousValue, currentValue) => {
    return previousValue + currentValue;
  }, 0);

  priceSum.value = formatWithCurrency(sumRaw);
};

const priceChange = (itemPrice, index) => {
  priceSumItems.value[index] = itemPrice;

  calcSum();
};

type QuantityRule = 'lowest' | 'highest' | 'multiple';

const showAmountWarning = async ({ rule, item }: { rule: QuantityRule; item: Item }) => {
  const translationKeys = {
    lowest: 'addToCart.LowestQty.Note',
    highest: 'addToCart.HighestQty.Note',
    multiple: 'addToCart.MultipleQty.Note',
  };
  const amountValue =
    rule === 'lowest' ? item.lowestQty : rule === 'highest' ? item.highestQty : item.multipleQty;
  const translation = storeTranslations.translations?.[translationKeys[rule]] || '';

  amountMessage.value = translation.replace(
    /\{amountValue\}|###amountValue###|#<amountValue>#|X/g,
    String(amountValue || 1),
  );
  showAmountMessage.value = false;
  await nextTick();
  showAmountMessage.value = true;
};

const updateQuantity = ({ item, quantity }) => {
  successMessage.value = undefined;

  if (isInitialized.value) {
    isUpdating.value = true;
    updateQuantityToUrl(updateUrl.value, {
      itemid: item.id,
      quantity: quantity.toString(),
      country: props.currentCountry,
      container: props.config.container,
      shopContainer: props.config.shopContainer,
      language: props.currentLanguage?.replace('EN-US', 'EN'),
    })
      .then((response) => {
        if ((response as ResponseError).error_message) {
          hasError.value = true;
          errorText.value = (response as ResponseError).error_message;
          isUpdating.value = false;
        } else {
          hasError.value = false;
          successMessage.value =
            storeTranslations.translations?.['shopCart.success.quantityUpdate'] ||
            'Item quantity updated successfully';
          isUpdating.value = false;
          getCart();
        }
      })
      .catch((error) => {
        console.error(error);
        hasError.value = true;
        errorText.value = error;
        isUpdating.value = false;
      });
  }
};

const removeItem = (item: Item, index: number) => {
  isItemLoading.value = {
    id: item.id,
    isLoading: true,
  };
  priceSumItems.value.splice(index, 1);

  removeItemToUrl(removeUrl.value, {
    itemid: item.id,
    country: props.currentCountry,
    container: props.config.container,
    shopContainer: props.config.shopContainer,
    language: props.currentLanguage?.replace('EN-US', 'EN'),
  })
    .then(() => {
      getCart();
      isItemLoading.value = {
        id: item.id,
        isLoading: false,
      };
      calcSum();
    })
    .catch((error) => {
      console.error(error);
      hasError.value = true;
      errorText.value = error.message;
      isItemLoading.value = {
        id: item.id,
        isLoading: false,
      };
    });
};

const shouldFlashShopPopper = (event) => {
  if (!event.detail) {
    return;
  }

  shouldFlashPopper.value = true;
  isItemLoading.value = {
    id: event.detail?.id || event.detail?.items?.[0]?.id,
    isLoading: true,
  };

  openAddToCartPopper(event.detail);
};

const openAddToCartPopper = (data) => {
  if (data.items) {
    newItem.value = data.items[0];
    isItemLoading.value = {
      id: data.items[0]?.id,
      isLoading: false,
    };
  }

  setTimeout(() => {
    return (shouldFlashPopper.value = false);
  }, 3000);
};

const handleShopCartUpdate = () => getCart();

watch(
  () => shopCartStore.isShopCartOpen,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      isShopCartOpen.value = newValue;

      if (isMobile.value) {
        if (newValue) {
          document.body.classList.add('noScroll');
        } else {
          if (document.body.classList.contains('noScroll')) {
            document.body.classList.remove('noScroll');
          }
        }
      }
    }
  },
);

watch(
  priceSum,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      totalPrice.value = newValue;
    }
  },
  { immediate: true },
);

watch(shouldFlashPopper, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    shopCartStore.setShopCartFlashing(newValue);
  }
});

watch(
  cartData,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && newValue.pricesum) {
      priceSum.value = formatWithCurrency(newValue.pricesum);
    }
  },
  { immediate: true },
);

onBeforeMount(() => {
  getCart();

  disposeNexwiseBridge = initNexwiseProductWizardBridge({
    onResolvedItem: (item) => {
      window.dispatchEvent(
        new CustomEvent(CustomEvents.ADD_TO_CART, {
          detail: {
            items: [
              {
                id: item.id,
                quantity: item.quantity,
                pricesingle: item.pricesingle,
                productType: item.productType,
                source: 'Product Wizard',
              },
            ],
          },
        }),
      );

      window.dispatchEvent(new CustomEvent(CustomEvents.UPDATE_SHOP_CART));
    },
  });

  window.addEventListener(CustomEvents.UPDATE_SHOP_CART, handleShopCartUpdate, false);
  window.addEventListener(CustomEvents.ADD_TO_CART, shouldFlashShopPopper, false);
});

onMounted(() => {
  loading.value = false;
});

onUnmounted(() => {
  window.removeEventListener(CustomEvents.UPDATE_SHOP_CART, handleShopCartUpdate, false);
  window.removeEventListener(CustomEvents.ADD_TO_CART, shouldFlashShopPopper, false);

  disposeNexwiseBridge?.();
  disposeNexwiseBridge = null;
});
</script>

<template>
  <div v-show="!loading" :class="$style.container">
    <button
      aria-controls="shopping-cart"
      :aria-expanded="isShopCartOpen"
      :aria-label="storeTranslations.translations?.['shopCart.title']"
      :class="$style.cartButton"
      :disabled="shouldDisableButton"
      @click="() => toggleCartClick()"
    >
      <Icon name="shopping_cart" size="large" />
      <span
        v-if="cartData.items?.length"
        :aria-label="storeTranslations.translations?.['shopCart.quantity']"
        :class="$style.quantity"
        >{{ cartData.quantity }}</span
      >
      <span class="accessible-link-text">{{
        storeTranslations.translations?.['shopCart.label']
      }}</span>
    </button>
    <ShopCartPopper
      v-if="shouldFlashPopper"
      id="shopCartPopper"
      :is-cart-updating="isUpdating"
      :is-item-loading="isItemLoading"
      :quantity="newItem ? newItem.quantity : 0"
      :select-options="[]"
      :shipping-link="shippingLink"
      :shop-cart-checkout-url="shopCartCheckoutUrl"
      :shop-cart-items="shouldFlashPopper && newItem ? [newItem] : []"
      :shop-cart-url="shopCartUrl"
      :total-price="totalPrice"
    />
    <ShopCartPopper
      v-else-if="cartData.items?.length"
      id="shopping-cart"
      :is-cart-updating="isUpdating"
      :is-item-loading="isItemLoading"
      :quantity="cartData.quantity"
      :select-options="selectOptions.value"
      :shipping-link="shippingLink"
      :shop-cart-checkout-url="shopCartCheckoutUrl"
      :shop-cart-items="cartData.items"
      :shop-cart-url="shopCartUrl"
      :total-price="totalPrice"
      @amount-warning="showAmountWarning"
      @new-quantity="updateQuantity"
      @price-change="priceChange"
      @remove-item="removeItem"
    />
  </div>
  <!-- GTM Data Layer - Always in DOM, updates reactively -->
  <div
    id="gtm-new-item-added"
    data-gtm-event="item-added"
    :style="{ visibility: 'hidden', display: 'none' }"
  >
    <input
      id="gtm-added-item-id"
      data-gtm-new="id"
      type="hidden"
      :value="newItem ? newItem.id : ''"
    />

    <input
      id="gtm-added-item-quantity"
      data-gtm-new="quantity"
      type="hidden"
      :value="newItem ? newItem.quantity : 0"
    />
    <input
      id="gtm-added-item-price"
      data-gtm-new="pricesingle"
      type="hidden"
      :value="newItem ? newItem.pricesingle : 0"
    />
    <input
      id="gtm-added-item-type"
      data-gtm-new="productType"
      type="hidden"
      :value="newItem ? newItem.productType : ''"
    />
    <input
      id="gtm-added-item-source"
      data-gtm-new="source"
      type="hidden"
      :value="newItem ? newItem.source : 'unknown'"
    />
  </div>

  <Notifications
    :message="successMessage"
    :open="!!successMessage"
    pattern="toast"
    type="success"
  />

  <Notifications :message="amountMessage" :open="showAmountMessage" pattern="toast" type="info" />

  <Notifications
    :message="errorText ? errorText : ''"
    :open="hasError"
    pattern="persistent"
    type="error"
    @update:open="hasError = $event"
  />
</template>

<style lang="scss" module>
@forward './ShopCart1XP.scss';
</style>
